// Part of settingsPages, split by screen to keep files short; ../settingsPages.ts merges the parts.
export const en = {
  migration: {
    moveFromQuickBooks: 'Move from QuickBooks',
    couldNotLoad: 'Could not load the migration',
    setDateFirst: 'Set the conversion date first',
    finished: 'The move from QuickBooks is finished',
    backToChecklist: 'Back to the checklist',
    back: 'Back',
    next: 'Next',
    rowIssue: 'Row {{row}}: {{message}}',
    debit: 'Debit',
    credit: 'Credit',
    openingBalances: {
      title: 'Opening balances',
      description:
        'Import the QuickBooks trial balance as of the day before the conversion date and map each account to this chart of accounts.',
      staffOnly: 'Only accounting staff can import opening balances',
      setDateFirstHint: 'Opening balances are dated the day before the conversion date.',
      finishedHint:
        'The books are locked through the opening date, so opening balances no longer change.',
      stepsLabel: 'Import steps',
      step: {
        upload: 'Upload',
        map: 'Map accounts',
        review: 'Review',
        done: 'Done',
      },
      how: {
        saved: 'Saved',
        number: 'Number',
        path: 'Path',
        name: 'Name',
      },
      dropTitle: 'Drag and drop the QuickBooks trial balance here, or browse',
      dropHint:
        'Reports › Trial balance, accrual basis, as of <b>{{date}}</b>, exported to Excel (.xlsx) or CSV. Leave the title rows in.',
      fileSummary_one: '{{count}} accounts.',
      fileSummary_other: '{{count}} accounts.',
      fileSummaryAsOf_one: 'as of {{date}} · {{count}} accounts.',
      fileSummaryAsOf_other: 'as of {{date}} · {{count}} accounts.',
      notAsOf: 'The file is not as of {{date}}, the day before the conversion date.',
      matchedNote:
        'Accounts were matched by a saved mapping, number, sub-account path or name; check the rest. A/R and A/P come in through the open invoices and unpaid bills.',
      qbAccount: 'QuickBooks account',
      ledgerAccount: 'Outss Accounting account',
      matchedBy: 'Matched by',
      account: 'Account',
      throughInvoices: 'Comes in through open invoices; not posted here',
      throughBills: 'Comes in through unpaid bills; not posted here',
      accountFor: 'Account for {{label}}',
      chooseAccount: 'Choose an account',
      chosen: 'Chosen',
      notMatched: 'Not matched',
      total: 'Total',
      outOfBalance: 'Out of balance by {{amount}}',
      unmapped_one: '{{count}} not mapped',
      unmapped_other: '{{count}} not mapped',
      balanced: 'Balanced; every account mapped',
      reviewSummary_one:
        'One opening-balance entry dated <b>{{date}}</b> will post {{count}} balances. A/R less A/P, <amt>{{amount}}</amt>, waits in Opening balance equity until the open invoices and unpaid bills are imported; then it is zero.',
      reviewSummary_other:
        'One opening-balance entry dated <b>{{date}}</b> will post {{count}} balances. A/R less A/P, <amt>{{amount}}</amt>, waits in Opening balance equity until the open invoices and unpaid bills are imported; then it is zero.',
      earlierReversed: 'The earlier opening entry is reversed.',
      posting: 'Posting…',
      post: 'Post opening balances',
      importOpenInvoices: 'Import open invoices',
    },
    openItems: {
      staffOnly: 'Only accounting staff can import open items',
      finishedHint: 'Enter later invoices and bills as new transactions.',
      receivable: {
        title: 'Open invoices',
        description:
          'Bring over the invoices and credits still open in QuickBooks the day before the conversion date, so they can be paid and aged here.',
        inTrialBalance: 'A/R in the trial balance',
        dropTitle: 'Drag and drop the QuickBooks A/R ageing detail here, or browse',
        dropHint:
          'Reports › A/R ageing detail, as of <b>{{date}}</b>, exported to Excel (.xlsx) or CSV. Customers are matched by display name, so import them first.',
        who: 'Customer',
        view: 'View invoices',
      },
      payable: {
        title: 'Unpaid bills',
        description:
          'Bring over the bills and debit notes still open in QuickBooks the day before the conversion date, so they can be paid and aged here.',
        inTrialBalance: 'A/P in the trial balance',
        dropTitle: 'Drag and drop the QuickBooks A/P ageing detail here, or browse',
        dropHint:
          'Reports › A/P ageing detail, as of <b>{{date}}</b>, exported to Excel (.xlsx) or CSV. Vendors are matched by display name, so import them first.',
        who: 'Vendor',
        view: 'View bills',
      },
      openItems: 'Open items',
      difference: 'Difference',
      doesNotTie: 'Does not tie',
      ties: 'Ties',
      noTrialBalance:
        'The opening trial balance is not imported yet, so there is nothing to tie these items to.',
      importOpeningBalances: 'Import opening balances',
      importedSummary: 'Imported {{ok}} of {{total}} open items ({{amount}}).',
      readySummary: '{{ok}} of {{total}} open items are ready to import ({{amount}}).',
      errorsSkipped: '{{n}} with errors were skipped.',
      errorsWillBeSkipped: '{{n}} with errors will be skipped.',
      noErrors: 'No errors found.',
      tieAfter: 'The tie-out above shows the result after the import.',
      onlyErrors: 'Show only rows with errors',
      downloadErrors: 'Download error report',
      col: {
        row: 'Row',
        date: 'Date',
        type: 'Type',
        number: 'No.',
        dueDate: 'Due date',
        amount: 'Amount',
        openBalance: 'Open balance',
        status: 'Status',
        errors: 'Errors',
      },
      status: {
        error: 'Error',
        imported: 'Imported',
        ready: 'Ready',
      },
      importAnother: 'Import another file',
      importing: 'Importing…',
      importItems_one: 'Import {{count}} item',
      importItems_other: 'Import {{count}} items',
    },
    history: {
      title: 'QuickBooks import history',
      description:
        'Every check and import of QuickBooks transactions for this client, with what each brought over.',
      importTransactions: 'Import transactions',
      couldNotLoad: 'Could not load the imports',
      empty: 'No QuickBooks imports yet',
      emptyHint: 'Import transactions from Move from QuickBooks once the client is connected.',
      col: {
        range: 'Range',
        import: 'Import',
        status: 'Status',
        imported: 'Imported',
        skipped: 'Skipped',
        notImported: 'Not imported',
        by: 'By',
        finished: 'Finished',
      },
      open: 'Open',
      transactions: 'Transactions',
      mode: {
        full_history: 'Full history',
        after_conversion: 'After the conversion date',
      },
      status: {
        planning: 'Checking QuickBooks',
        planned: 'Checked',
        queued: 'Waiting to start',
        running: 'Importing',
        completed: 'Completed',
        failed: 'Failed',
        cancelled: 'Cancelled',
      },
    },
    run: {
      title: 'Imported from QuickBooks',
      description:
        'Each QuickBooks transaction of this import and what it became in Outss Accounting.',
      couldNotLoad: 'Could not load the transactions',
      col: {
        date: 'Date',
        quickBooks: 'QuickBooks',
        became: 'Became',
        amount: 'Amount',
        note: 'Note',
      },
      outcome: {
        document: 'Document',
        payment: 'Payment',
        journal: 'Journal entry',
        applied: 'Credits applied',
        skipped: 'Skipped',
      },
      kind: {
        invoice: 'Invoice',
        credit_note: 'Credit note',
        sales_receipt: 'Sales receipt',
        quote: 'Quote',
        bill: 'Bill',
        debit_note: 'Debit note',
        expense: 'Expense',
        billPayment: 'Bill payment',
      },
    },
    importFixes: {
      title: 'Fix problems',
      toImport_one: '{{count}} transactions still to import',
      toImport_other: '{{count}} transactions still to import',
      allImported: 'Every transaction is imported',
      fixedOrReviewed: '{{n}} fixed or reviewed',
      done: 'Done',
      notImported: 'Not imported',
      note: 'Note',
      working: 'Working…',
      vatTitle: 'Turn on VAT registration',
      vatDescriptionFrom:
        'QuickBooks charged VAT from {{date}}. Enter the client’s TRN and the date its registration took effect (on the FTA certificate). Company settings are updated and the failed transactions are imported again.',
      vatDescription:
        'QuickBooks charged VAT from these transactions. Enter the client’s TRN and the date its registration took effect (on the FTA certificate). Company settings are updated and the failed transactions are imported again.',
      trn: 'TRN',
      trnHint: '15 digits',
      trnError: 'The TRN has 15 digits.',
      registrationDate: 'VAT registration date',
      dateError: 'Enter the date VAT registration took effect.',
      dateHint: 'The first transaction with VAT is dated {{date}}.',
      submit: 'Turn on VAT and import',
    },
  },
};

export const ar = {
  migration: {
    moveFromQuickBooks: 'الانتقال من QuickBooks',
    couldNotLoad: 'تعذّر تحميل عملية الانتقال',
    setDateFirst: 'حدّد تاريخ التحويل أولاً',
    finished: 'اكتمل الانتقال من QuickBooks',
    backToChecklist: 'العودة إلى قائمة المهام',
    back: 'رجوع',
    next: 'التالي',
    rowIssue: 'الصف {{row}}: {{message}}',
    debit: 'مدين',
    credit: 'دائن',
    openingBalances: {
      title: 'الأرصدة الافتتاحية',
      description:
        'استورد ميزان المراجعة من QuickBooks كما في اليوم السابق لتاريخ التحويل، واربط كل حساب بحساب في دليل الحسابات هذا.',
      staffOnly: 'استيراد الأرصدة الافتتاحية متاح لموظفي المحاسبة فقط',
      setDateFirstHint: 'تُؤرَّخ الأرصدة الافتتاحية باليوم السابق لتاريخ التحويل.',
      finishedHint:
        'الدفاتر مقفلة حتى تاريخ الافتتاح، لذا لم تعد الأرصدة الافتتاحية قابلة للتغيير.',
      stepsLabel: 'خطوات الاستيراد',
      step: {
        upload: 'الرفع',
        map: 'ربط الحسابات',
        review: 'المراجعة',
        done: 'تم',
      },
      how: {
        saved: 'ربط محفوظ',
        number: 'الرقم',
        path: 'المسار',
        name: 'الاسم',
      },
      dropTitle: 'اسحب ميزان المراجعة من QuickBooks وأفلته هنا، أو تصفّح',
      dropHint:
        'Reports › Trial balance، على أساس الاستحقاق، كما في <b>{{date}}</b>، مُصدَّراً إلى Excel ‏(.xlsx) أو CSV. أبقِ صفوف العنوان كما هي.',
      fileSummary_zero: 'لا حسابات.',
      fileSummary_one: 'حساب واحد.',
      fileSummary_two: 'حسابان.',
      fileSummary_few: '{{count}} حسابات.',
      fileSummary_many: '{{count}} حساباً.',
      fileSummary_other: '{{count}} حساب.',
      fileSummaryAsOf_zero: 'كما في {{date}} · لا حسابات.',
      fileSummaryAsOf_one: 'كما في {{date}} · حساب واحد.',
      fileSummaryAsOf_two: 'كما في {{date}} · حسابان.',
      fileSummaryAsOf_few: 'كما في {{date}} · {{count}} حسابات.',
      fileSummaryAsOf_many: 'كما في {{date}} · {{count}} حساباً.',
      fileSummaryAsOf_other: 'كما في {{date}} · {{count}} حساب.',
      notAsOf: 'الملف ليس كما في {{date}}، أي اليوم السابق لتاريخ التحويل.',
      matchedNote:
        'رُبطت الحسابات بحسب ربط محفوظ أو الرقم أو مسار الحساب الفرعي أو الاسم؛ راجع البقية. تأتي الذمم المدينة والذمم الدائنة عبر الفواتير المفتوحة والفواتير غير المدفوعة.',
      qbAccount: 'حساب QuickBooks',
      ledgerAccount: 'حساب Outss Accounting',
      matchedBy: 'طريقة الربط',
      account: 'الحساب',
      throughInvoices: 'يأتي عبر الفواتير المفتوحة؛ لا يُرحَّل هنا',
      throughBills: 'يأتي عبر فواتير الموردين غير المدفوعة؛ لا يُرحَّل هنا',
      accountFor: 'الحساب المقابل لـ {{label}}',
      chooseAccount: 'اختر حساباً',
      chosen: 'مُختار',
      notMatched: 'غير مربوط',
      total: 'الإجمالي',
      outOfBalance: 'غير متوازن بفارق {{amount}}',
      unmapped_zero: 'لا حسابات غير مربوطة',
      unmapped_one: 'حساب واحد غير مربوط',
      unmapped_two: 'حسابان غير مربوطين',
      unmapped_few: '{{count}} حسابات غير مربوطة',
      unmapped_many: '{{count}} حساباً غير مربوط',
      unmapped_other: '{{count}} حساب غير مربوط',
      balanced: 'متوازن؛ وكل الحسابات مربوطة',
      reviewSummary_zero:
        'سيُرحِّل قيد رصيد افتتاحي واحد بتاريخ <b>{{date}}</b> لا أرصدة. يبقى الفرق بين الذمم المدينة والذمم الدائنة، <amt>{{amount}}</amt>، في حقوق ملكية الرصيد الافتتاحي إلى أن تُستورد الفواتير المفتوحة والفواتير غير المدفوعة، ثم يصبح صفراً.',
      reviewSummary_one:
        'سيُرحِّل قيد رصيد افتتاحي واحد بتاريخ <b>{{date}}</b> رصيداً واحداً. يبقى الفرق بين الذمم المدينة والذمم الدائنة، <amt>{{amount}}</amt>، في حقوق ملكية الرصيد الافتتاحي إلى أن تُستورد الفواتير المفتوحة والفواتير غير المدفوعة، ثم يصبح صفراً.',
      reviewSummary_two:
        'سيُرحِّل قيد رصيد افتتاحي واحد بتاريخ <b>{{date}}</b> رصيدين. يبقى الفرق بين الذمم المدينة والذمم الدائنة، <amt>{{amount}}</amt>، في حقوق ملكية الرصيد الافتتاحي إلى أن تُستورد الفواتير المفتوحة والفواتير غير المدفوعة، ثم يصبح صفراً.',
      reviewSummary_few:
        'سيُرحِّل قيد رصيد افتتاحي واحد بتاريخ <b>{{date}}</b> {{count}} أرصدة. يبقى الفرق بين الذمم المدينة والذمم الدائنة، <amt>{{amount}}</amt>، في حقوق ملكية الرصيد الافتتاحي إلى أن تُستورد الفواتير المفتوحة والفواتير غير المدفوعة، ثم يصبح صفراً.',
      reviewSummary_many:
        'سيُرحِّل قيد رصيد افتتاحي واحد بتاريخ <b>{{date}}</b> {{count}} رصيداً. يبقى الفرق بين الذمم المدينة والذمم الدائنة، <amt>{{amount}}</amt>، في حقوق ملكية الرصيد الافتتاحي إلى أن تُستورد الفواتير المفتوحة والفواتير غير المدفوعة، ثم يصبح صفراً.',
      reviewSummary_other:
        'سيُرحِّل قيد رصيد افتتاحي واحد بتاريخ <b>{{date}}</b> {{count}} رصيد. يبقى الفرق بين الذمم المدينة والذمم الدائنة، <amt>{{amount}}</amt>، في حقوق ملكية الرصيد الافتتاحي إلى أن تُستورد الفواتير المفتوحة والفواتير غير المدفوعة، ثم يصبح صفراً.',
      earlierReversed: 'ويُعكس القيد الافتتاحي السابق.',
      posting: 'جارٍ الترحيل…',
      post: 'ترحيل الأرصدة الافتتاحية',
      importOpenInvoices: 'استيراد الفواتير المفتوحة',
    },
    openItems: {
      staffOnly: 'استيراد البنود المفتوحة متاح لموظفي المحاسبة فقط',
      finishedHint: 'أدخل الفواتير اللاحقة كمعاملات جديدة.',
      receivable: {
        title: 'الفواتير المفتوحة',
        description:
          'انقل فواتير المبيعات والإشعارات الدائنة التي كانت لا تزال مفتوحة في QuickBooks في اليوم السابق لتاريخ التحويل، لتتمكن من تحصيلها وتعميرها هنا.',
        inTrialBalance: 'الذمم المدينة في ميزان المراجعة',
        dropTitle: 'اسحب تقرير A/R ageing detail من QuickBooks وأفلته هنا، أو تصفّح',
        dropHint:
          'Reports › A/R ageing detail، كما في <b>{{date}}</b>، مُصدَّراً إلى Excel ‏(.xlsx) أو CSV. يُطابَق العملاء بالاسم المعروض، لذا استوردهم أولاً.',
        who: 'العميل',
        view: 'عرض الفواتير',
      },
      payable: {
        title: 'الفواتير غير المدفوعة',
        description:
          'انقل فواتير الموردين والإشعارات المدينة التي كانت لا تزال مفتوحة في QuickBooks في اليوم السابق لتاريخ التحويل، لتتمكن من سدادها وتعميرها هنا.',
        inTrialBalance: 'الذمم الدائنة في ميزان المراجعة',
        dropTitle: 'اسحب تقرير A/P ageing detail من QuickBooks وأفلته هنا، أو تصفّح',
        dropHint:
          'Reports › A/P ageing detail، كما في <b>{{date}}</b>، مُصدَّراً إلى Excel ‏(.xlsx) أو CSV. يُطابَق الموردون بالاسم المعروض، لذا استوردهم أولاً.',
        who: 'المورد',
        view: 'عرض فواتير الموردين',
      },
      openItems: 'البنود المفتوحة',
      difference: 'الفرق',
      doesNotTie: 'غير مطابق',
      ties: 'مطابق',
      noTrialBalance:
        'لم يُستورد ميزان المراجعة الافتتاحي بعد، لذا لا يوجد ما تُطابَق به هذه البنود.',
      importOpeningBalances: 'استيراد الأرصدة الافتتاحية',
      importedSummary: 'استُورد {{ok}} من أصل {{total}} بنداً مفتوحاً ({{amount}}).',
      readySummary: '{{ok}} من أصل {{total}} بنداً مفتوحاً جاهزة للاستيراد ({{amount}}).',
      errorsSkipped: 'تم تخطي {{n}} بها أخطاء.',
      errorsWillBeSkipped: 'سيتم تخطي {{n}} بها أخطاء.',
      noErrors: 'لم يُعثر على أخطاء.',
      tieAfter: 'تُظهر المطابقة أعلاه النتيجة بعد الاستيراد.',
      onlyErrors: 'عرض الصفوف التي بها أخطاء فقط',
      downloadErrors: 'تنزيل تقرير الأخطاء',
      col: {
        row: 'الصف',
        date: 'التاريخ',
        type: 'النوع',
        number: 'الرقم',
        dueDate: 'تاريخ الاستحقاق',
        amount: 'المبلغ',
        openBalance: 'الرصيد المفتوح',
        status: 'الحالة',
        errors: 'الأخطاء',
      },
      status: {
        error: 'خطأ',
        imported: 'مستورد',
        ready: 'جاهز',
      },
      importAnother: 'استيراد ملف آخر',
      importing: 'جارٍ الاستيراد…',
      importItems_zero: 'لا بنود للاستيراد',
      importItems_one: 'استيراد بند واحد',
      importItems_two: 'استيراد بندين',
      importItems_few: 'استيراد {{count}} بنود',
      importItems_many: 'استيراد {{count}} بنداً',
      importItems_other: 'استيراد {{count}} بند',
    },
    history: {
      title: 'سجل الاستيراد من QuickBooks',
      description:
        'كل عمليات الفحص والاستيراد لمعاملات QuickBooks لهذه الشركة، وما نقلته كل عملية.',
      importTransactions: 'استيراد المعاملات',
      couldNotLoad: 'تعذّر تحميل عمليات الاستيراد',
      empty: 'لا توجد عمليات استيراد من QuickBooks بعد',
      emptyHint: 'استورد المعاملات من صفحة الانتقال من QuickBooks بعد ربط الشركة.',
      col: {
        range: 'الفترة',
        import: 'الاستيراد',
        status: 'الحالة',
        imported: 'المستورد',
        skipped: 'المتخطّى',
        notImported: 'غير المستورد',
        by: 'بواسطة',
        finished: 'تاريخ الانتهاء',
      },
      open: 'فتح',
      transactions: 'المعاملات',
      mode: {
        full_history: 'السجل الكامل',
        after_conversion: 'بعد تاريخ التحويل',
      },
      status: {
        planning: 'جارٍ فحص QuickBooks',
        planned: 'تم الفحص',
        queued: 'بانتظار البدء',
        running: 'جارٍ الاستيراد',
        completed: 'مكتمل',
        failed: 'فشل',
        cancelled: 'أُلغي',
      },
    },
    run: {
      title: 'مستورد من QuickBooks',
      description:
        'كل معاملة QuickBooks في عملية الاستيراد هذه وما أصبحت عليه في Outss Accounting.',
      couldNotLoad: 'تعذّر تحميل المعاملات',
      col: {
        date: 'التاريخ',
        quickBooks: 'QuickBooks',
        became: 'أصبحت',
        amount: 'المبلغ',
        note: 'ملاحظة',
      },
      outcome: {
        document: 'مستند',
        payment: 'دفعة',
        journal: 'قيد يومية',
        applied: 'أرصدة دائنة مطبّقة',
        skipped: 'متخطّاة',
      },
      kind: {
        invoice: 'فاتورة',
        credit_note: 'إشعار دائن',
        sales_receipt: 'إيصال مبيعات',
        quote: 'عرض سعر',
        bill: 'فاتورة مورد',
        debit_note: 'إشعار مدين',
        expense: 'مصروف',
        billPayment: 'دفعة فاتورة مورد',
      },
    },
    importFixes: {
      title: 'إصلاح المشكلات',
      toImport_zero: 'لا معاملات متبقية للاستيراد',
      toImport_one: 'معاملة واحدة متبقية للاستيراد',
      toImport_two: 'معاملتان متبقيتان للاستيراد',
      toImport_few: '{{count}} معاملات متبقية للاستيراد',
      toImport_many: '{{count}} معاملة متبقية للاستيراد',
      toImport_other: '{{count}} معاملة متبقية للاستيراد',
      allImported: 'استُوردت كل المعاملات',
      fixedOrReviewed: '{{n}} مُصلحة أو تمت مراجعتها',
      done: 'تم',
      notImported: 'غير مستورد',
      note: 'ملاحظة',
      working: 'جارٍ التنفيذ…',
      vatTitle: 'تفعيل التسجيل في ضريبة القيمة المضافة',
      vatDescriptionFrom:
        'احتسب QuickBooks ضريبة القيمة المضافة اعتباراً من {{date}}. أدخل رقم التسجيل الضريبي (TRN) للشركة وتاريخ سريان تسجيلها (المذكور في شهادة الهيئة الاتحادية للضرائب). تُحدَّث إعدادات الشركة ويُعاد استيراد المعاملات التي فشلت.',
      vatDescription:
        'احتسب QuickBooks ضريبة القيمة المضافة على هذه المعاملات. أدخل رقم التسجيل الضريبي (TRN) للشركة وتاريخ سريان تسجيلها (المذكور في شهادة الهيئة الاتحادية للضرائب). تُحدَّث إعدادات الشركة ويُعاد استيراد المعاملات التي فشلت.',
      trn: 'رقم التسجيل الضريبي (TRN)',
      trnHint: '15 رقماً',
      trnError: 'يتكون رقم التسجيل الضريبي من 15 رقماً.',
      registrationDate: 'تاريخ التسجيل في ضريبة القيمة المضافة',
      dateError: 'أدخل تاريخ سريان التسجيل في ضريبة القيمة المضافة.',
      dateHint: 'أول معاملة عليها ضريبة القيمة المضافة مؤرخة في {{date}}.',
      submit: 'تفعيل الضريبة والاستيراد',
    },
  },
};
